/**
 * Utility functions for Optimizely CDN image resizing
 * Documentation: https://docs.developers.optimizely.com/digital-experience-platform/docs/image-resizing-using-cdn
 */

/**
 * Valid CDN image format options
 */
export const IMAGE_FORMATS = {
    AUTO: 'auto',
    WEBP: 'webp',
    JPEG: 'jpeg',
    PNG: 'png',
    AVIF: 'avif'
}

/**
 * Valid fit options for image resizing
 */
export const FIT_OPTIONS = {
    SCALE_DOWN: 'scale-down', // Resize to fit within bounds, but never upscale
    CONTAIN: 'contain', // Resize maintaining aspect ratio
    COVER: 'cover', // Resize and crop to fill dimensions
    CROP: 'crop', // Crop to exact dimensions
    PAD: 'pad' // Resize and pad with background color
}

/**
 * Responsive image breakpoints (matches Chakra UI breakpoints)
 */
export const BREAKPOINTS = {
    MOBILE: 'base', // < 768px
    TABLET: 'md', // >= 768px
    DESKTOP: 'lg' // >= 1024px
}

/**
 * Common image size presets based on usage
 */
export const IMAGE_PRESETS = {
    // Hero/Banner images
    HERO_DESKTOP: {width: 1920, quality: 85, format: IMAGE_FORMATS.AUTO},
    HERO_TABLET: {width: 1024, quality: 85, format: IMAGE_FORMATS.AUTO},
    HERO_MOBILE: {width: 768, quality: 85, format: IMAGE_FORMATS.AUTO},

    // Category/Product tiles
    CATEGORY_TILE: {width: 400, quality: 80, format: IMAGE_FORMATS.AUTO},
    PRODUCT_TILE: {width: 300, quality: 80, format: IMAGE_FORMATS.AUTO},

    // Content blocks
    CONTENT_LARGE: {width: 1200, quality: 85, format: IMAGE_FORMATS.AUTO},
    CONTENT_MEDIUM: {width: 800, quality: 85, format: IMAGE_FORMATS.AUTO},
    CONTENT_SMALL: {width: 400, quality: 80, format: IMAGE_FORMATS.AUTO},

    // Thumbnails
    THUMBNAIL_LARGE: {width: 200, quality: 80, format: IMAGE_FORMATS.AUTO},
    THUMBNAIL_SMALL: {width: 100, quality: 75, format: IMAGE_FORMATS.AUTO}
}

/**
 * Build CDN image URL with optimization parameters
 * @param {string} imageUrl - Original image URL
 * @param {Object} options - CDN transformation options
 * @param {number} options.width - Target width in pixels
 * @param {number} options.height - Target height in pixels
 * @param {string} options.fit - Fit mode (scale-down, contain, cover, crop, pad)
 * @param {number} options.quality - Quality (1-100, default 85)
 * @param {string} options.format - Output format (auto, webp, jpeg, png, avif)
 * @param {number} options.dpr - Device pixel ratio (1, 2, 3)
 * @param {string} options.gravity - Crop gravity (auto, center, top, bottom, left, right)
 * @param {string} options.background - Background color for pad fit (hex without #)
 * @returns {string} CDN-optimized image URL
 */
export const buildCdnImageUrl = (imageUrl, options = {}) => {
    if (!imageUrl || typeof imageUrl !== 'string') {
        return imageUrl
    }

    // Only process URLs from digitalgdi.com domain
    if (!imageUrl.includes('digitalgdi.com')) {
        return imageUrl
    }

    // Check if URL already has CDN parameters
    if (imageUrl.includes('/cdn-cgi/image/')) {
        return imageUrl
    }

    const {
        width,
        height,
        fit = FIT_OPTIONS.SCALE_DOWN,
        quality = 85,
        format = IMAGE_FORMATS.AUTO,
        dpr,
        gravity,
        background
    } = options

    const params = []

    if (width) params.push(`width=${width}`)
    if (height) params.push(`height=${height}`)
    if (fit) params.push(`fit=${fit}`)
    if (quality) params.push(`quality=${quality}`)
    if (format) params.push(`format=${format}`)
    if (dpr) params.push(`dpr=${dpr}`)
    if (gravity) params.push(`gravity=${gravity}`)
    if (background) params.push(`background=${background}`)

    // No params specified, return original URL
    if (params.length === 0) {
        return imageUrl
    }

    // Parse URL to inject CDN parameters
    try {
        const url = new URL(imageUrl)
        const cdnParams = params.join(',')

        // Insert cdn-cgi/image parameters after the domain
        const newPath = `/cdn-cgi/image/${cdnParams}${url.pathname}`

        return `${url.protocol}//${url.host}${newPath}${url.search}`
    } catch (error) {
        console.error('Error building CDN URL:', error)
        return imageUrl
    }
}

/**
 * Build responsive image URLs for different breakpoints
 * @param {string} imageUrl - Original image URL
 * @param {Object} breakpointOptions - Options for each breakpoint
 * @param {Object} breakpointOptions.mobile - Mobile options
 * @param {Object} breakpointOptions.tablet - Tablet options
 * @param {Object} breakpointOptions.desktop - Desktop options
 * @returns {Object} Object with URLs for each breakpoint
 */
export const buildResponsiveCdnUrls = (imageUrl, breakpointOptions = {}) => {
    const {
        mobile = IMAGE_PRESETS.HERO_MOBILE,
        tablet = IMAGE_PRESETS.HERO_TABLET,
        desktop = IMAGE_PRESETS.HERO_DESKTOP
    } = breakpointOptions

    return {
        mobile: buildCdnImageUrl(imageUrl, mobile),
        tablet: buildCdnImageUrl(imageUrl, tablet),
        desktop: buildCdnImageUrl(imageUrl, desktop)
    }
}

/**
 * Get optimized image URL based on preset
 * @param {string} imageUrl - Original image URL
 * @param {string} presetName - Preset name from IMAGE_PRESETS
 * @param {Object} overrides - Options to override preset values
 * @returns {string} Optimized image URL
 */
export const getOptimizedImageUrl = (imageUrl, presetName, overrides = {}) => {
    const preset = IMAGE_PRESETS[presetName]

    if (!preset) {
        console.warn(`Unknown image preset: ${presetName}`)
        return imageUrl
    }

    return buildCdnImageUrl(imageUrl, {...preset, ...overrides})
}

/**
 * Generate srcSet for responsive images
 * @param {string} imageUrl - Original image URL
 * @param {Array<number>} widths - Array of widths for srcSet
 * @param {Object} options - Additional CDN options
 * @returns {string} srcSet string
 */
export const generateSrcSet = (imageUrl, widths = [400, 800, 1200, 1600, 1920], options = {}) => {
    if (!imageUrl || !imageUrl.includes('digitalgdi.com')) {
        return ''
    }

    return widths
        .map((width) => {
            const url = buildCdnImageUrl(imageUrl, {...options, width})
            return `${url} ${width}w`
        })
        .join(', ')
}

/**
 * Generate sizes attribute for responsive images
 * @param {Object} breakpointSizes - Sizes for each breakpoint
 * @returns {string} sizes attribute value
 */
export const generateSizesAttribute = (breakpointSizes = {}) => {
    const {mobile = '100vw', tablet = '50vw', desktop = '33vw'} = breakpointSizes

    return [`(max-width: 767px) ${mobile}`, `(max-width: 1023px) ${tablet}`, desktop].join(', ')
}

/**
 * Check if URL is eligible for CDN optimization
 * @param {string} imageUrl - Image URL to check
 * @returns {boolean} True if URL can be optimized
 */
export const canOptimizeImage = (imageUrl) => {
    if (!imageUrl || typeof imageUrl !== 'string') {
        return false
    }

    return imageUrl.includes('digitalgdi.com') && !imageUrl.includes('/cdn-cgi/image/')
}

/**
 * Calculate responsive sizes attribute based on grid columns
 * @param {Object} options - Configuration options
 * @param {number} options.mobileColumns - Number of columns on mobile (default: 1)
 * @param {number} options.tabletColumns - Number of columns on tablet (default: 2)
 * @param {number} options.desktopColumns - Number of columns on desktop (default: 3)
 * @param {number} options.gap - Gap between items in px (default: 16)
 * @returns {string} sizes attribute value
 */
export const calculateGridImageSizes = ({
    mobileColumns = 1,
    tabletColumns = 2,
    desktopColumns = 3,
    gap = 16
} = {}) => {
    // Calculate viewport percentage accounting for gaps
    // Formula: (100vw - (gaps * gapSize)) / columns
    const calculateVw = (columns) => {
        if (columns === 1) return '100vw'
        const gapCount = columns - 1
        return `calc((100vw - ${gapCount * gap}px) / ${columns})`
    }

    const mobileSizing = calculateVw(mobileColumns)
    const tabletSizing = calculateVw(tabletColumns)
    const desktopSizing = calculateVw(desktopColumns)

    return [
        `(max-width: 767px) ${mobileSizing}`,
        `(max-width: 1023px) ${tabletSizing}`,
        desktopSizing
    ].join(', ')
}

/**
 * Calculate appropriate image widths for grid/carousel items
 * Returns widths in [mobile, tablet, desktop] order for ResponsiveMediaBlock
 *
 * @param {number} desktopColumns - Number of columns/slides on desktop
 * @param {number} tabletColumns - Number of columns/slides on tablet (optional)
 * @param {number} mobileColumns - Number of columns/slides on mobile (optional)
 * @returns {Array<number>} Array of [mobileWidth, tabletWidth, desktopWidth] in pixels
 */
export const calculateGridImageWidths = (
    desktopColumns,
    tabletColumns = null,
    mobileColumns = null
) => {
    // Use sensible defaults based on desktop columns
    const effectiveTabletCols = tabletColumns || Math.min(Math.floor(desktopColumns / 2), 4)
    const effectiveMobileCols = mobileColumns || Math.min(Math.floor(desktopColumns / 4), 2)

    // Calculate approximate image width per column at each breakpoint
    // Use typical viewport widths and account for padding/gaps
    const mobileWidth = Math.ceil((375 - 32) / Math.max(effectiveMobileCols, 1)) // 375px - 16px padding each side
    const tabletWidth = Math.ceil((768 - 48) / Math.max(effectiveTabletCols, 1)) // 768px - padding
    const desktopWidth = Math.ceil((1440 - 48) / Math.max(desktopColumns, 1)) // 1440px typical desktop

    // Return in [mobile, tablet, desktop] order for ResponsiveMediaBlock
    return [mobileWidth, tabletWidth, desktopWidth]
}
